import React, { useEffect, useState } from 'react'
import DatePicker from '../../common/datePicker/DatePicker'
import CustomSelect from '../../common/dropdown/Dropdown'
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import addline from '../../../assets/icons/add-line (1).svg'
import apiService from '../../../services/apiService';

const genderOptions = [{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }];

const experienceOptions = [{ value: '1_3_years', label: '1-3 years' }, { value: '3_5_years', label: '3-5 years' }, { value: '5_8_years', label: '5-8 years' }, { value: '8_10_years', label: '8-10 years' }, { value: '10_years', label: '+10 years' }];

const NewRequest = ({ onClose, fetchRequests }) => {

    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState('');
    const [dropdown, setDropdown] = useState({
        specialties: "",
        medSpecialties: "",
        needCategory: "",
    })

    const initialValues = {

        fields: [
            {
                practitioners: 1,
                specialty: {},
                medicalSpecialty: {},
                type: {},
                category: {},
                startDate: "",
                endDate: "",
                exp: {},
            },
        ],

    };

    const validationSchema = Yup.object({
        fields: Yup.array().of(
            Yup.object({
                practitioners: Yup.number()
                    .min(1, 'Must be at least 1')
                    .required('Number of Practitioners is required'),

                specialty: Yup.object()
                    .nullable()
                    .required('Specialty is required')
                    .test(
                        "is-not-empty",
                        "Specialty is required",
                        (value) => value && Object.keys(value).length > 0
                    ),

                medicalSpecialty: Yup.object()
                    .nullable()
                    .required('Medical Specialty is required')
                    .test(
                        "is-not-empty",
                        "Medical Specialty is required",
                        (value) => value && Object.keys(value).length > 0
                    ),

                type: Yup.object()
                    .nullable()
                    .required('Gender is required')
                    .test(
                        "is-not-empty",
                        "Gender is required",
                        (value) => value && Object.keys(value).length > 0
                    ),

                category: Yup.object()
                    .nullable()
                    .required('Category is required')
                    .test(
                        "is-not-empty",
                        "Category is required",
                        (value) => value && Object.keys(value).length > 0
                    ),

                startDate: Yup.string().required('Start Date is required'),
                endDate: Yup.string().required('End Date is required'),

                exp: Yup.object()
                    .nullable()
                    .required('Experience is required')
                    .test(
                        "is-not-empty",
                        "Experience is required",
                        (value) => value && Object.keys(value).length > 0
                    ),
            })
        ),
    });


    const transformPayload = (fields,) => {
        const jobPositions = fields.map(field => ({
            speciality_id: field.specialty._id,
            medical_speciality: field.medicalSpecialty._id,
            no_of_practitioners: field.practitioners,
            need_category: field.category._id,
            experience: field.exp.value,
            gender: field.type.value,
            available_at: field.startDate // Use the start date as available_at
        }));

        return {
            notes: note,
            job_positions: jobPositions
        };
    };

    const handleSubmit = async (values) => {
        const payload = transformPayload(values.fields);
        try {
            setLoading(true)
            const response = await apiService.post("job-requests", payload, {});
            if (response.code === 201) {
                fetchRequests();
                toast.success('New request created successfully.', { toastId: "19" });
                setTimeout(() => {
                    onClose();
                    setLoading(false);
                }, 1000);
            }

        } catch (error) {
            setLoading(false)
            toast.error(error?.response?.data?.error || "Unexpected Error", { toastId: "98" });
        }
    };

    const handleCategoryReset = (setFieldValue, index) => {
        // Reset startDate and endDate if category changes

        setFieldValue(`fields[${index}].startDate`, null);
        setFieldValue(`fields[${index}].endDate`, null);
        return
    };

    const handleFieldChange = (index, name, value, setFieldValue, field) => {

        // Update the value in Formik state
        setFieldValue(`fields[${index}].${name}`, value);

        // Handle category change to reset startDate and endDate
        if (name === "category") {
            setTimeout(() => {
                handleCategoryReset(setFieldValue, index)
            }, 100);
        };

        // Handle startDate to auto-calculate endDate based on category.days
        if (name === "startDate" && field?.category?.days) {
            // Ensure category.days is a valid number before using it
            const days = parseInt(field.category.days);
            if (!isNaN(days)) {
                const startDate = new Date(value);
                const endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + days);

                // Format the endDate as ISO string (YYYY-MM-DD)
                const formattedEndDate = endDate.toISOString().split('T')[0];
                setTimeout(() => {
                    setFieldValue(`fields[${index}].endDate`, formattedEndDate);
                }, 100);
            } else {
                // If category.days is not a valid number, do not calculate endDate
                console.error("Invalid category days value:", field?.category?.days);
            }
        }
    };


    const addFieldSet = (setFieldValue, fields) => {
        const newFieldSet = {
            practitioners: 1,
            specialty: {},
            medicalSpecialty: {},
            type: "",
            category: {},
            startDate: "",
            endDate: "",
            exp: "",
        };
        setFieldValue("fields", [...fields, newFieldSet]);
    };

    const removeFieldSet = (index, setFieldValue, values) => {
        // Filter out the field at the given index
        const updatedFields = values.fields.filter((_, i) => i !== index);

        // Update Formik's state with the new fields array
        setFieldValue("fields", updatedFields);
    };

    const transformApiResponse = (data) => {
        return data.map(item => ({
            ...item,
            value: item._id,
            label: item.name,
        }));
    };

    const fetchDropdownData = async () => {
        try {
            const [specialtiesResponse, medSpecialtiesResponse, needCategoryResponse] = await Promise.all([
                apiService.get('moh_manager/all/specialties'),
                apiService.get('moh_manager/all/medSpecialties'),
                apiService.get('moh_manager/all/needCategory'),
            ]);

            const specialties = transformApiResponse(specialtiesResponse?.data || []);
            const medSpecialties = transformApiResponse(medSpecialtiesResponse?.data || []);
            const needCategory = transformApiResponse(needCategoryResponse?.data || []);

            setDropdown({
                specialties,
                medSpecialties,
                needCategory,
            });
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const init = () => {
        fetchDropdownData();
    };

    useEffect(() => {
        init();
    }, [])


    return (
        <div>
            <div class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div class="bg-white w-full max-w-3xl h-[95vh] p-6 rounded-lg shadow-lg overflow-y-auto">

                    <div class="flex justify-between items-center mb-6 border-b pb-4">
                        <div>
                            <h2 class="text-lg font-semibold">New Request</h2>

                        </div>
                        <button class="text-gray-500 hover:text-red-500" onClick={onClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                            // Log the form values to the console
                            handleSubmit(values)
                        }}
                    >
                        {({ values, setFieldValue, handleChange, errors, touched }) => (
                            <Form>
                                {values?.fields?.map((field, index) => (
                                    <>
                                        <div key={index} className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                                                <CustomSelect
                                                    value={field?.specialty?.label}
                                                    onFieldSelect={(e) => {
                                                        handleFieldChange(index, "specialty", e, setFieldValue)
                                                    }}
                                                    options={dropdown.specialties}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].specialty`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Medical Specialty</label>
                                                <CustomSelect
                                                    value={field?.medicalSpecialty?.label}
                                                    name={`medicalSpecialty-${index}`}
                                                    zIndex={999}
                                                    onFieldSelect={(e) => {
                                                        handleFieldChange(index, "medicalSpecialty", e, setFieldValue)
                                                    }}
                                                    options={dropdown.medSpecialties}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].medicalSpecialty`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div className="w-full max-w-sm">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">No of Practitioners</label>
                                                <div className="h-[44px] flex items-center border rounded-[12px]">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleFieldChange(index, "practitioners", Math.max(1, field.practitioners - 1), setFieldValue)
                                                        }
                                                        className="w-10 h-[40px] rounded-[12px] flex justify-center items-center bg-white hover:bg-gray-200 text-lg"
                                                    >
                                                        -
                                                    </button>

                                                    <input
                                                        name={`practitioners-${index}`}
                                                        value={field.practitioners}
                                                        readOnly
                                                        className="w-full text-center focus:outline-none border-0"
                                                    />

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleFieldChange(index, "practitioners", field.practitioners + 1, setFieldValue)
                                                        }
                                                        className="w-9 h-[40px] rounded-[12px] flex justify-center items-center bg-white hover:bg-gray-200 text-lg"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                                <ErrorMessage
                                                    name={`fields[${index}].practitioners`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Need Category</label>
                                                <CustomSelect
                                                    value={field?.category?.label}
                                                    zIndex={888}
                                                    name="category"
                                                    onFieldSelect={(e) => {
                                                        handleFieldChange(index, "category", e, setFieldValue)
                                                    }
                                                    }
                                                    options={dropdown.needCategory}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].category`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                                <DatePicker
                                                    placement={"down"}
                                                    name="startDate"
                                                    value={field.startDate}
                                                    onChange={(e) => {
                                                        handleFieldChange(index, "startDate", e, setFieldValue, field)
                                                        handleCategoryReset(setFieldValue);
                                                    }
                                                    }
                                                    minDate={new Date()}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].startDate`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                                <DatePicker
                                                    value={field.endDate}
                                                    disabled={true}
                                                    onChange={handleChange}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].endDate`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                                                <CustomSelect
                                                    value={field?.exp?.label}
                                                    onFieldSelect={(e) => handleFieldChange(index, "exp", e, setFieldValue)}
                                                    options={experienceOptions}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].exp`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                                                <CustomSelect
                                                    value={field?.type?.label}
                                                    onFieldSelect={(e) => handleFieldChange(index, "type", e, setFieldValue)}
                                                    options={genderOptions}
                                                />
                                                <ErrorMessage
                                                    name={`fields[${index}].type`}
                                                    component="div"
                                                    className="text-red-500 text-[12px]"
                                                />
                                            </div>


                                        </div>
                                        <div>

                                            <button
                                                type="button"
                                                onClick={() => removeFieldSet(index, setFieldValue, values)}
                                                className="mb-4 mt-4 w-full bg-[white] text-[red] px-4 py-2 rounded border border-[#008556] flex items-center justify-center gap-2" // Added flex for icon and text alignment/qqq
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addFieldSet(setFieldValue, values.fields)} // Pass setFieldValue and values.fields correctly
                                    className="mb-4 mt-4 w-full bg-[white] text-[#008556] px-4 py-2 rounded border border-[#008556] flex items-center justify-center gap-2" // Added flex for icon and text alignment

                                >
                                    <img src={addline} alt="Add More" className="w-4 h-4" /> {/* Optionally adjust the size of the image */}
                                    Add More
                                </button>
                                <div class="mt-4">
                                    <label class="block text-sm font-medium text-gray-700 mb-1">Note<label className='text-gray-500'>{"(Optional)"}</label></label>
                                    <textarea
                                        placeholder="Type a message"
                                        rows="5"
                                        class="w-full border rounded-lg p-2 text-gray-700"
                                        value={note} // Bind the state to the textarea
                                        onChange={(event) => setNote(event.target.value)} // Update the state when the value changes
                                    ></textarea>
                                </div>

                                <div className='pt-3 pb-1'>
                                    <hr />
                                </div>
                                <div className="mt-2" />

                                <div className="flex justify-between mt-3 gap-4">
                                    <button onClick={onClose} type="button" className="w-full px-4 py-2 bg-[white] text-[#000929] border text-gray-700 rounded-lg">
                                        Cancel
                                    </button>

                                    <button disabled={loading} type="submit" className="w-full px-4 py-2 bg-[#008556] text-white rounded-lg">
                                        {loading ? (
                                            <svg
                                                aria-hidden="true"
                                                className="inline w-4 h-4 text-gray-200 animate-spin  fill-green-500"
                                                viewBox="0 0 100 101"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                    fill="currentFill"
                                                />
                                            </svg>
                                        ) : (
                                            'Submit'
                                        )}
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>


                </div>
            </div>
        </div>

    )
}

export default NewRequest